import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { FileAudio, FileText, ImageIcon, Play, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { SuggestLineReport } from '@/app/components/icons/SuggestLineReport';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MENTION_COLOR_MAP } from '@/app/constants';
import { cn } from '@/app/utils';
import { useMentionContext } from '../context';

// 扩展NodeViewProps接口以包含自定义选项
interface MentionNodeViewProps extends Omit<NodeViewProps, 'extension'> {
  extension: NodeViewProps['extension'] & {
    options: {
      showTooltip: boolean;
      className?: string;
      variant?: 'default' | 'media-chip';
    };
  };
}

const MentionNodeView = ({ node, extension }: MentionNodeViewProps) => {
  const t = useTranslations('create.proMode');
  const { getPrefix } = useMentionContext();
  const showTooltip = extension.options.showTooltip;
  const mentionClassName = extension.options.className;
  const variant = extension.options.variant ?? 'default';

  const prefix = useMemo(() => {
    if (variant === 'media-chip') {
      const preview = typeof node.attrs.preview === 'string' ? node.attrs.preview : '';
      const mediaType = typeof node.attrs.mediaType === 'string' ? node.attrs.mediaType : '';

      if (preview && mediaType === 'image') {
        return (
          <span className="mr-1 flex size-5 overflow-hidden rounded-full bg-black/20 ring-1 ring-white/20">
            <img src={preview} alt="" className="size-full object-cover" />
          </span>
        );
      }

      if (preview && mediaType === 'video') {
        return (
          <span className="relative mr-1 flex size-5 overflow-hidden rounded-full bg-black/20 ring-1 ring-white/20">
            <video src={preview} className="size-full object-cover" muted playsInline preload="metadata" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
              <Play className="size-2.5 fill-current" />
            </span>
          </span>
        );
      }

      const contextPrefix = getPrefix?.(node.attrs.id, node.attrs.label);
      if (contextPrefix) return contextPrefix;

      let Icon = FileText;
      if (mediaType === 'image') Icon = ImageIcon;
      else if (mediaType === 'video') Icon = Video;
      else if (mediaType === 'audio') Icon = FileAudio;

      return (
        <span className="mr-1 inline-flex size-5 items-center justify-center rounded-full bg-white/15 text-[#9EC1FF]">
          <Icon className="size-3.5" />
        </span>
      );
    }

    if (getPrefix) {
      return getPrefix(node.attrs.id, node.attrs.label);
    }
    return null;
  }, [getPrefix, node.attrs.id, node.attrs.label, node.attrs.mediaType, node.attrs.preview, variant]);

  const alignMiddle = !!prefix;

  return (
    <NodeViewWrapper
      as="span"
      data-drag-handle
      className={cn('mention mx-0.5 cursor-grab', (showTooltip || alignMiddle) && 'align-middle')}
    >
      <CustomTooltip content={showTooltip ? t('mentionTip') : ''}>
        <span
          className={cn(
            'relative inline-flex items-center gap-1 rounded-6 px-1.5 leading-5',
            variant === 'media-chip' && 'rounded-10 px-2 py-0.5 font-medium text-sm',
            mentionClassName,
            node.attrs.color === MENTION_COLOR_MAP.forbidden ? 'text-system-white64' : '',
          )}
          style={
            {
              whiteSpace: 'nowrap',
              background:
                variant === 'media-chip'
                  ? 'rgba(80,141,255,0.10)'
                  : showTooltip
                    ? '#333333'
                    : `linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%), ${node.attrs.color || '#333333'}`,
            } as React.CSSProperties
          }
        >
          {showTooltip && <SuggestLineReport className="shrink-0 text-base text-system-white" />}
          {prefix}
          {node.attrs.label}
        </span>
      </CustomTooltip>
    </NodeViewWrapper>
  );
};

export default MentionNodeView;
